import {getIntegrationHost} from "@/utils/integration-host";

/**
 * На ПК VK, Exe и Фотострана отдают игре айфрейм фиксированной высоты (около 900 px), какой бы
 * ни была вкладка: при окне 893 px низ канваса уходит за край. Изнутри айфрейма размер
 * окна не виден, innerHeight и visualViewport вернут те же 900. Сколько айфрейма реально на
 * экране, сообщает только IntersectionObserver: он работает и на чужом ориджине.
 *
 * Меряем до запуска игры и заново, когда меняется сам айфрейм. Если айфрейм остался прежним,
 * а поменялось только окно, событие resize внутри не приходит - тогда до перезагрузки игра
 * живёт со старым замером. В полноэкранном режиме замер не действует: там игре доступен весь экран.
 */
export default class FrameViewport {

    /** Меньше этого считаем замер ошибочным: лучше срез снизу, чем игра в узкую полоску. */
    private static readonly MIN_HEIGHT: number = 400;

    /** Сколько ждём, пока родительская страница применит новую высоту айфрейма. */
    private static readonly FRAME_RESIZE_WAIT: number = 200;

    /** В фоновой вкладке наблюдатель молчит, держать из-за этого загрузку нельзя. */
    private static readonly READING_TIMEOUT: number = 500;

    private static readonly READING_PAUSE: number = 100;
    private static readonly MAX_READINGS: number = 5;

    /** События resize идут пачками. Должно быть заметно меньше задержки применения в MainView (400 мс). */
    private static readonly REFRESH_DELAY: number = 100;

    private static visible: number = 0;

    private static refreshTimer: number | undefined = undefined;

    public static limit(height: number): number {
        return FrameViewport.getVisible() > 0 ? Math.min(height, FrameViewport.visible) : height;
    }

    public static getVisible(): number {
        return FrameViewport.isFullscreen() ? 0 : FrameViewport.visible;
    }

    public static isNeeded(): boolean {
        const ua = typeof navigator !== 'undefined' && navigator.userAgent ? navigator.userAgent : '';
        // iPad в Safari представляется Mac-ом, выдаёт его только сенсорный экран.
        const isIpad = /Macintosh/i.test(ua) && navigator.maxTouchPoints > 1;
        return (FrameViewport.isVkHost() || FrameViewport.isExeHost() || FrameViewport.isFotoHost())
            && window.top !== window.self
            && typeof IntersectionObserver !== 'undefined'
            && !isIpad
            && !/Android|iPhone|iPad|Mobile/i.test(ua);
    }

    /** Ошибка здесь не должна помешать запуску игры: без замера она просто будет как раньше. */
    public static async fit(): Promise<void> {
        if (!FrameViewport.isNeeded()) {
            return;
        }
        try {
            const isExe = FrameViewport.isExeHost();
            const startHeight = window.innerHeight;

            // У Exe высоту айфрейма задаём сами. Растягиваем его с запасом, иначе окно выше 900 px
            // останется неизвестным: замер не покажет больше, чем высота самого айфрейма.
            if (isExe) {
                FrameViewport.setExeHeight(Math.ceil(window.screen.height));
                await FrameViewport.pause(FrameViewport.FRAME_RESIZE_WAIT);
            }

            await FrameViewport.measure();

            if (isExe) {
                FrameViewport.setExeHeight(FrameViewport.visible || startHeight);
            }
        } catch (e) {
            console.log('FrameViewport.fit error', e);
        }
    }

    /**
     * Айфрейм изменился: окно растянули и площадка подстроила его под окно, либо вход и выход из
     * полного экрана. Новое значение подхватит MainView, сверяя размеры через 400 мс.
     * У Exe высоту задаём мы сами, замер от её же изменения зациклил бы подгонку.
     */
    public static refresh(): void {
        if (!FrameViewport.isNeeded() || FrameViewport.isExeHost()) {
            return;
        }
        window.clearTimeout(FrameViewport.refreshTimer);
        FrameViewport.refreshTimer = window.setTimeout(() => {
            if (!FrameViewport.isFullscreen()) {
                FrameViewport.measure().catch(e => console.log('FrameViewport.refresh error', e));
            }
        }, FrameViewport.REFRESH_DELAY);
    }

    private static async measure(): Promise<void> {
        document.documentElement.scrollIntoView();
        const visible = await FrameViewport.readSettled();
        if (visible >= FrameViewport.MIN_HEIGHT) {
            FrameViewport.visible = visible;
        }
    }

    private static isFullscreen(): boolean {
        const doc = document as any;
        return !!(doc.fullscreenElement || doc.webkitFullscreenElement || doc.mozFullScreenElement);
    }

    /** Родительская страница может докручиваться плавно: ждём двух одинаковых замеров подряд. */
    private static async readSettled(): Promise<number> {
        let last = await FrameViewport.read();
        for (let i = 1; i < FrameViewport.MAX_READINGS; i++) {
            await FrameViewport.pause(FrameViewport.READING_PAUSE);
            const current = await FrameViewport.read();
            if (current === last) {
                return current;
            }
            last = current;
        }
        return 0;
    }

    private static read(): Promise<number> {
        return new Promise(resolve => {
            const timer = window.setTimeout(() => {
                observer.disconnect();
                resolve(0);
            }, FrameViewport.READING_TIMEOUT);

            const observer = new IntersectionObserver(entries => {
                window.clearTimeout(timer);
                observer.disconnect();
                const entry = entries[entries.length - 1];
                resolve(entry.isIntersecting ? Math.floor(entry.intersectionRect.height) : 0);
            });
            observer.observe(document.documentElement);
        });
    }

    private static setExeHeight(height: number): void {
        // @ts-ignore
        window.exeApi.setHeight(height);
    }

    private static pause(ms: number): Promise<void> {
        return new Promise(resolve => window.setTimeout(resolve, ms));
    }

    private static isVkHost(): boolean {
        const host = getIntegrationHost();
        return host.includes('vk.zazer') || host.includes('vk.lgtest');
    }

    private static isExeHost(): boolean {
        const host = getIntegrationHost();
        return host.includes('exe.zazer') || host.includes('exe.lgtest');
    }

    private static isFotoHost(): boolean {
        return getIntegrationHost().includes('foto.zazer');
    }
}
